/**
 * Deep link dai widget della home del telefono verso le schermate dell'app.
 *
 * Schema: `lumen://home`, `lumen://timer`, `lumen://agenda`, `lumen://progress`.
 * Sono accettati anche gli URL https dell'app (universal link) con lo stesso
 * percorso, così lo stesso codice serve per entrambi i sistemi operativi.
 */

export type WidgetTarget = "home" | "timer" | "agenda" | "progress";

const TARGET_ROUTES: Record<WidgetTarget, string> = {
  home: "/",
  timer: "/tools?tool=timer",
  agenda: "/planner",
  progress: "/stats",
};

export const WIDGET_TARGETS: WidgetTarget[] = ["home", "timer", "agenda", "progress"];

function isTarget(v: string): v is WidgetTarget {
  return (WIDGET_TARGETS as string[]).includes(v);
}

/** Percorso interno dell'app per un contenuto del widget. */
export function routeForTarget(target: WidgetTarget): string {
  return TARGET_ROUTES[target];
}

/** URL da usare nel widget nativo (tocco → apre l'app sulla schermata giusta). */
export function deepLinkForTarget(target: WidgetTarget): string {
  return `lumen://${target}`;
}

/**
 * Traduce un URL in arrivo (deep link o universal link) nel percorso interno.
 * Restituisce `null` se l'URL non è riconosciuto: in quel caso l'app non naviga.
 */
export function resolveDeepLink(url: string): string | null {
  if (!url || typeof url !== "string") return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  if (parsed.protocol === "lumen:") {
    // lumen://timer  → host "timer";  lumen:///timer → pathname "/timer"
    const raw = (parsed.hostname || parsed.pathname.replace(/^\/+/, "")).split("/")[0] ?? "";
    const key = raw.toLowerCase();
    if (isTarget(key)) return routeForTarget(key);
    return key ? `/${key}` : "/";
  }

  if (parsed.protocol === "http:" || parsed.protocol === "https:") {
    const path = `${parsed.pathname}${parsed.search}`;
    const first = parsed.pathname.replace(/^\/+/, "").split("/")[0] ?? "";
    if (isTarget(first)) return routeForTarget(first);
    return path && path !== "/" ? path : "/";
  }

  return null;
}
